import React, { useState } from 'react';
import { Clock, X } from 'lucide-react';

interface OrderETAModalProps {
  isOpen: boolean;
  restaurantName: string;
  itemName: string;
  onConfirm: (minutes: number, notes: string) => void;
  onCancel: () => void;
}

export const OrderETAModal: React.FC<OrderETAModalProps> = ({
  isOpen,
  restaurantName,
  itemName,
  onConfirm,
  onCancel,
}) => {
  const [minutes, setMinutes] = useState(30);
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (minutes > 0) {
      onConfirm(minutes, notes);
      setMinutes(30); // Reset for next time
      setNotes('');
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-2xl p-6 border-2 border-purple-500/30 shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
              <span className="text-2xl">🍴</span>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Order Placed!</h3>
              <p className="text-sm text-gray-400">Start tracking your delivery</p>
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="mb-6 p-3 rounded-lg bg-purple-900/20 border border-purple-500/20">
          <p className="text-sm font-semibold text-purple-300">{restaurantName}</p>
          <p className="text-xs text-gray-300">{itemName}</p>
        </div>

        {/* ETA Input */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-purple-200 mb-2 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            How long did they say it will take?
          </label>
          
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={minutes}
              onChange={(e) => setMinutes(Math.max(1, parseInt(e.target.value) || 30))}
              min="1"
              max="120"
              className="flex-1 px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white text-center text-2xl font-bold focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50"
            />
            <span className="text-lg text-gray-300 font-semibold">minutes</span>
          </div>

          {/* Quick Select Buttons */}
          <div className="flex gap-2 mt-3">
            {[15, 20, 30, 45, 60].map((min) => (
              <button
                key={min}
                onClick={() => setMinutes(min)}
                className={`flex-1 px-2 py-1.5 rounded text-xs font-semibold transition-all ${
                  minutes === min
                    ? 'bg-purple-600 text-white border-2 border-purple-400'
                    : 'bg-purple-900/20 text-purple-300 border border-purple-500/30 hover:bg-purple-900/40'
                }`}
              >
                {min}m
              </button>
            ))}
          </div>
        </div>

        {/* Notes (Optional) */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-purple-200 mb-2">
            Special notes? <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Leave at door, Ring bell, etc..."
            className="w-full px-3 py-2 bg-black/40 border border-purple-500/30 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-gray-700/50 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg font-semibold hover:from-emerald-500 hover:to-green-500 transition-colors shadow-lg"
          >
            Start Tracking
          </button>
        </div>
      </div>
    </div>
  );
};
